import { useEffect } from 'react';
import { NavLink, Navigate, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { CenteredSpinner } from '../components/Spinner.tsx';
import { ProgressBar } from '../components/ProgressBar.tsx';
import { CompanyStatusBadge } from '../components/StatusBadge.tsx';
import { ExpiryBanner } from '../components/ExpiryBanner.tsx';
import { NavIcon, type IconName } from '../components/NavIcon.tsx';
import { useCompanyAuth } from '../lib/auth/company.tsx';
import { useBranding } from '../lib/branding.tsx';
import { companyApi, ApiError } from '../lib/api.ts';
import type { Company } from '../lib/types.ts';

const NAV: { to: string; label: string; icon: IconName; end?: boolean }[] = [
  { to: '/c/questionnaire', label: 'Questionnaire', icon: 'list-checks' },
  { to: '/c/training', label: 'Training', icon: 'graduation-cap' },
  { to: '/c/info', label: 'Company info', icon: 'building' },
  { to: '/c/drivers', label: 'Drivers', icon: 'users' },
  { to: '/c/assets', label: 'Assets', icon: 'truck' },
  { to: '/c/corrective-actions', label: 'Corrective actions', icon: 'clipboard-check' },
  { to: '/c/documents', label: 'Documents', icon: 'file-text' },
  { to: '/c/library', label: 'Library', icon: 'book' },
];

function initials(name: string | undefined): string {
  if (!name) return 'U';
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return 'U';
  const first = parts[0] ?? '';
  if (parts.length === 1) return first.slice(0, 2).toUpperCase() || 'U';
  const last = parts[parts.length - 1] ?? '';
  return `${first[0] ?? ''}${last[0] ?? ''}`.toUpperCase() || 'U';
}

export function CompanyShell() {
  const { session, loading, signOut } = useCompanyAuth();
  const { branding } = useBranding();
  const location = useLocation();
  const navigate = useNavigate();
  const brandName = branding.companyName?.replace(/ Safety.*$/i, '') ?? 'Locate2u';

  const { data, isLoading, error } = useQuery({
    queryKey: ['me'],
    queryFn: () => companyApi.get<{ company: Company }>('/api/me'),
    enabled: !!session,
  });

  useEffect(() => {
    if (error instanceof ApiError && error.status === 401) {
      void signOut();
      navigate('/c/expired', { replace: true });
    }
  }, [error, signOut, navigate]);

  if (loading) return <CenteredSpinner />;
  if (!session) {
    return <Navigate to="/c/expired" state={{ from: location }} replace />;
  }
  if (isLoading) return <CenteredSpinner />;
  const company = data?.company;

  return (
    <div className="flex min-h-screen bg-white">
      <aside
        className="relative hidden w-72 shrink-0 flex-col border-r md:flex"
        style={{ background: '#FFFFFF', borderColor: '#E2E8F0' }}
      >
        {/* Brand */}
        <div className="flex items-center px-6 pb-5 pt-6">
          {branding.logoUrl ? (
            <img src={branding.logoUrl} alt={brandName} className="h-10 w-auto" />
          ) : (
            <span
              className="text-[15px] font-bold tracking-tight"
              style={{ color: 'var(--brand-primary)' }}
            >
              {brandName}
            </span>
          )}
        </div>

        {/* Company status card */}
        {company && (
          <div
            className="mx-5 mb-4 rounded-lg border bg-white p-3"
            style={{ borderColor: '#E2E8F0' }}
          >
            <div className="truncate text-[13px] font-semibold" style={{ color: '#0F172A' }}>
              {company.company_name}
            </div>
            <div className="mt-1.5">
              <CompanyStatusBadge status={company.status} />
            </div>
            <div className="mt-3">
              <ProgressBar value={company.progress} label="Progress" />
            </div>
          </div>
        )}

        {/* Nav */}
        <nav className="flex-1 overflow-y-auto px-4 pb-2">
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end ?? false}
              className={({ isActive }) =>
                `relative flex items-center gap-3 rounded-lg px-3 py-2 text-[13.5px] transition ${
                  isActive ? 'font-semibold' : 'font-medium hover:bg-slate-50'
                }`
              }
              style={({ isActive }) =>
                isActive ? { background: '#DBEAFE', color: '#1D4ED8' } : { color: '#475569' }
              }
            >
              {({ isActive }) => (
                <>
                  <NavIcon
                    name={item.icon}
                    size={17}
                    style={{ color: isActive ? '#1D4ED8' : '#64748B' }}
                  />
                  <span className="flex-1 text-left">{item.label}</span>
                </>
              )}
            </NavLink>
          ))}
        </nav>

        {/* Footer */}
        <div className="border-t px-4 pb-4 pt-3" style={{ borderColor: '#E2E8F0' }}>
          <div className="flex items-center gap-2 px-2 pb-3">
            <span
              className="h-[7px] w-[7px] rounded-full"
              style={{
                background: '#10B981',
                boxShadow: '0 0 8px rgba(16,185,129,0.5)',
              }}
            />
            <span className="flex-1 text-[11.5px]" style={{ color: '#475569' }}>
              All systems normal
            </span>
          </div>
          <button
            type="button"
            onClick={() => void signOut()}
            className="flex w-full items-center gap-3 rounded-lg border bg-white p-2 text-left transition hover:bg-slate-50"
            style={{ borderColor: '#E2E8F0' }}
            title="Sign out"
          >
            <div
              className="font-mono-eyebrow flex h-[30px] w-[30px] shrink-0 items-center justify-center rounded-full text-[11px] font-bold text-white"
              style={{ background: 'var(--brand-primary)' }}
            >
              {initials(company?.contact_name)}
            </div>
            <div className="min-w-0 flex-1">
              <div className="truncate text-[12.5px] font-semibold" style={{ color: '#0F172A' }}>
                {company?.contact_name ?? 'Account'}
              </div>
              <div className="truncate text-[10.5px]" style={{ color: '#64748B' }}>
                Sign out
              </div>
            </div>
            <NavIcon name="log-out" size={16} style={{ color: '#64748B' }} />
          </button>
          <div
            className="font-mono-eyebrow mt-3 text-center text-[10px] uppercase tracking-[0.2em]"
            style={{ color: '#64748B' }}
          >
            Powered by{' '}
            <a
              href="https://www.locate2u.com"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:underline"
              style={{ color: 'var(--brand-primary)' }}
            >
              Locate2u
            </a>
          </div>
        </div>
      </aside>

      <div className="flex min-w-0 flex-1 flex-col" style={{ background: '#F8FAFC' }}>
        {/* Mobile top bar */}
        <header
          className="flex items-center justify-between border-b px-4 py-3 md:hidden"
          style={{ background: '#FFFFFF', borderColor: '#E2E8F0' }}
        >
          <div className="flex min-w-0 items-center">
            {branding.logoUrl ? (
              <img src={branding.logoUrl} alt={brandName} className="h-8 w-auto" />
            ) : (
              <span
                className="truncate text-[14px] font-bold tracking-tight"
                style={{ color: 'var(--brand-primary)' }}
              >
                {brandName}
              </span>
            )}
          </div>
          <button
            type="button"
            onClick={() => void signOut()}
            className="rounded p-1.5 hover:bg-white"
            aria-label="Sign out"
          >
            <NavIcon name="log-out" size={18} style={{ color: '#64748B' }} />
          </button>
        </header>

        {/* Mobile nav tabs */}
        <nav
          className="flex gap-1 overflow-x-auto border-b px-4 md:hidden"
          style={{ background: '#FFFFFF', borderColor: '#E2E8F0' }}
        >
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end ?? false}
              className={({ isActive }) =>
                `whitespace-nowrap border-b-2 px-3 py-3 text-sm transition ${
                  isActive ? 'font-semibold' : 'border-transparent font-medium'
                }`
              }
              style={({ isActive }) =>
                isActive
                  ? { borderColor: 'var(--brand-primary)', color: 'var(--brand-primary)' }
                  : { color: '#475569' }
              }
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        {company && <ExpiryBanner company={company} />}

        <main className="w-full flex-1 px-8 pb-8 pt-5">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
